import type { HamPermissionChecksResult } from './ItamPermissionsContext.tsx';
import type { PermissionCheckResult } from './PermissionCheckResult.tsx';

const HAM_ADMIN_PERMISSION_ID = 'manage-ham';
const HAM_USER_PERMISSION_ID = 'view-ham';
const SERVICE_DESK_USER_PERMISSION_ID = 'jsd-user';
const JIRA_ADMIN_PERMISSION_ID = 'jira-admin';
const ASSETS_ADMIN_PERMISSION_ID = 'assets-admin';
const ASSETS_USER_PERMISSION_ID = 'assets-user';
const MANAGE_PERMISSION_ID = 'manage';
const WRITE_PERMISSION_ID = 'write';

const getServiceDeskSiteAri = (cloudId: string): string =>
	`ari:cloud:jira-servicedesk::site/${cloudId}`;
const getJiraSiteAri = (cloudId: string): string => `ari:cloud:jira::site/${cloudId}`;
const getAssetsSiteAri = (cloudId: string): string => `ari:cloud:cmdb::site/${cloudId}`;

/**
 * Combines the static (cloudId-derived) and HAM (workspace-scoped) permission
 * check results into the shared `HamPermissionChecksResult` shape consumed by
 * `useItamTenantContext`.
 */
export const buildHamPermissionChecksResult = (
	cloudId: string,
	assetsVerticalWorkspaceAri: string | null,
	staticResults: ReadonlyArray<PermissionCheckResult>,
	hamResults: ReadonlyArray<PermissionCheckResult>,
): HamPermissionChecksResult => {
	const serviceDeskSiteAri = getServiceDeskSiteAri(cloudId);
	const jiraSiteAri = getJiraSiteAri(cloudId);
	const assetsSiteAri = getAssetsSiteAri(cloudId);

	const permissions = {
		[HAM_ADMIN_PERMISSION_ID]: false,
		[HAM_USER_PERMISSION_ID]: false,
		[SERVICE_DESK_USER_PERMISSION_ID]: false,
		[JIRA_ADMIN_PERMISSION_ID]: false,
		[ASSETS_ADMIN_PERMISSION_ID]: false,
		[ASSETS_USER_PERMISSION_ID]: false,
	};

	for (const { permissionId, permitted, resourceId } of [...staticResults, ...hamResults]) {
		if (permissionId === HAM_ADMIN_PERMISSION_ID || permissionId === HAM_USER_PERMISSION_ID) {
			permissions[permissionId] = permitted;
		} else if (resourceId === serviceDeskSiteAri) {
			permissions[SERVICE_DESK_USER_PERMISSION_ID] = permitted;
		} else if (resourceId === jiraSiteAri) {
			permissions[JIRA_ADMIN_PERMISSION_ID] = permitted;
		} else if (resourceId === assetsSiteAri) {
			if (permissionId === MANAGE_PERMISSION_ID) {
				permissions[ASSETS_ADMIN_PERMISSION_ID] = permitted;
			} else if (permissionId === WRITE_PERMISSION_ID) {
				permissions[ASSETS_USER_PERMISSION_ID] = permitted;
			}
		}
	}

	return {
		assetsVerticalWorkspaceAri,
		permissions,
	};
};
